import {
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  Box,
} from "@mui/material";
import { AsyncImage } from "loadable-image";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { getDownloadURL, getStorage, uploadBytes, ref } from "firebase/storage";
import { CircularProgress } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Add from "@mui/icons-material/Add";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ReactQuill from "react-quill";
import * as yup from "yup";
import { AppContext } from "../../../../store/AppContext";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { CategorySelectionModal } from "./CategorySelectionModel";
import { attachProductCounts } from "../../../../utils/categoryProducts";

const AddNewCategory = () => {
  const isInitialRender = useRef(true);
  const { setLoader, categoryTabInfo, setCategoryTabInfo, setHeaderData } =
    useContext(AppContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const [activeTab, setActiveTab] = useState("productInfo"); // Track active tab
  const [bannerTabIndex, setBannerTabIndex] = useState(0); // 0 for desktop, 1 for mobile
  const [value1, setValue1] = useState(
    categoryTabInfo?.selectedCategory?.description || ""
  );
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState(
    categoryTabInfo?.selectedCategory?.images || []
  );
  const [desktopBanner, setDesktopBanner] = useState(
    categoryTabInfo?.selectedCategory?.desktopBanner || null
  );
  const [mobileBanner, setMobileBanner] = useState(
    categoryTabInfo?.selectedCategory?.mobileBanner || null
  );
  const [isSubcategory, setIsSubcategory] = useState(
    categoryTabInfo?.selectedCategory?.isSubcategory || false
  );
  const [submitted, setSubmitted] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const productInfoRef = useRef(null);
  const productMediaRef = useRef(null);
  const inventoryRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const schema = yup.object().shape({
    categoryName: yup.string().required("Category name is required"),
    order: yup
      .number()
      .typeError("Order must be a number")
      .min(0, "Order must be positive")
      .required("Order is required"),
    parentCategory: yup.object().when("isSubcategory", {
      is: true,
      then: () =>
        yup.object({
          categoryId: yup.string().required("Parent category ID is required"),
          categoryName: yup
            .string()
            .required("Parent category name is required"),
        }),
      otherwise: () => yup.object(),
    }),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: categoryTabInfo?.selectedCategory
      ? {
          ...categoryTabInfo.selectedCategory,
          order: categoryTabInfo.selectedCategory.order, // Ensure order is a number
        }
      : {
          categoryName: "",
          parentCategory: {},
          isSubcategory: false,
        },
    mode: "onBlur", // Only validate on submit
  });

  // Update form when isSubcategory changes
  useEffect(() => {
    setValue("isSubcategory", isSubcategory);

    // Only retrigger validation if the form has been submitted already
    if (submitted) {
      trigger();
    }
  }, [isSubcategory, setValue, trigger, submitted]);

  const handleSubcategoryChange = (event) => {
    setIsSubcategory(event.target.checked);
  };

  const onSubmit = (data) => {
    console.log("Form submitted:", data);
    setSubmitted(true);
    // Add form submission logic here
  };

  const handleFormSubmit = async (e) => {
    setSubmitted(true);
    // Let react-hook-form handle the actual submission
  };

  const quickMenu = [
    { title: "Information", ref: "productInfo" },
    { title: "Banner", ref: "productMedia" },
    { title: "Content", ref: "inventory" },
  ];

  const handleScrollToSection = (section) => {
    const sectionRefMapping = {
      productInfo: productInfoRef,
      productMedia: productMediaRef,
      inventory: inventoryRef,
    };

    setActiveTab(section);
    const sectionRef = sectionRefMapping[section];

    if (sectionRef && sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    // Close mobile drawer after navigation
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      setLoading(true);
      const storage = getStorage();
      const uploadPromises = Array.from(files).map(async (file) => {
        const storageRef = ref(storage, `category/${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        return downloadURL;
      });

      try {
        const uploadedImages = await Promise.all(uploadPromises);
        setImages((prev) => [...prev, ...uploadedImages]);
      } catch (error) {
        console.error("Error uploading images:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBannerUpload = async (event, type) => {
    setLoader(true);
    const file = event.target.files[0];
    if (file) {
      setLoading(true);
      const storage = getStorage();
      try {
        const storageRef = ref(storage, `banners/${type}/${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        if (type === "desktop") {
          setDesktopBanner(downloadURL);
        } else {
          setMobileBanner(downloadURL);
        }
        setLoader(false);
      } catch (error) {
        setLoader(false);
        console.error(`Error uploading ${type} banner:`, error);
      } finally {
        setLoader(false);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const sections = [
      { id: "productInfo", ref: productInfoRef },
      { id: "productMedia", ref: productMediaRef },
      { id: "inventory", ref: inventoryRef },
    ];

    const observerOptions = {
      root: scrollContainerRef.current,
      rootMargin: "0px",
      threshold: 0.9,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = sections.find(
            (section) => section.ref.current === entry.target
          )?.id;
          if (sectionId) {
            setActiveTab(sectionId);
          }
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    sections.forEach((section) => {
      if (section.ref.current) {
        observer.observe(section.ref.current);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section.ref.current) {
          observer.unobserve(section.ref.current);
        }
      });
      // Keep page / totalPages when leaving the form.
      setCategoryTabInfo((prev) => ({
        ...prev,
        addCategory: false,
        selectedCategory: null,
      }));
    };
  }, []);

  const AddNewCategories = async (data) => {
    setLoader(true);
    try {
      const categoriesCollection = collection(db, "categories");

      // Check if category already exists (based on a unique field, e.g., categoryName)
      const q = query(
        categoriesCollection,
        where("id", "==", categoryTabInfo?.selectedCategory?.id || "")
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Category exists, get the first matching document
        const existingDoc = querySnapshot.docs[0];
        const categoryRef = doc(db, "categories", existingDoc.id);
        const previousData = existingDoc.data(); // Get the previous data

        // Store previous data in the audit log before updating
        await addDoc(collection(db, "audit_logs"), {
          categoryId: existingDoc.id,
          categoryName: previousData.categoryName,
          previousData, // Store the full previous category data
          action: "Updated",
          timestamp: serverTimestamp(),
        });

        // Update the existing category with new data
        await updateDoc(categoryRef, {
          ...data,
          images,
          desktopBanner,
          mobileBanner,
          description: value1,
          updatedDate: serverTimestamp(),
        });

        // If this is a subcategory and parent has changed, update parent's subCategories array
        if (isSubcategory && data.parentCategory?.categoryId) {
          const parentCategoryRef = doc(
            db,
            "categories",
            data.parentCategory.categoryId
          );
          const parentCategoryDoc = await getDoc(parentCategoryRef);

          if (parentCategoryDoc.exists()) {
            const parentData = parentCategoryDoc.data();
            const subCategories = parentData.subCategories || [];

            // Only update if the subcategory ID isn't already in the array
            if (!subCategories.includes(existingDoc.id)) {
              await updateDoc(parentCategoryRef, {
                subCategories: [...subCategories, existingDoc.id],
              });
            }
          }
        }

        console.log("Category updated with ID: ", existingDoc.id);
      } else {
        // If category doesn't exist, add a new one
        const docRef = await addDoc(categoriesCollection, {
          ...data,
          images,
          desktopBanner,
          mobileBanner,
          description: value1,
          active: true,
          createdDate: serverTimestamp(),
        });

        // Update document with its own ID
        await updateDoc(doc(db, "categories", docRef.id), {
          id: docRef.id,
        });

        console.log("New category added with ID: ", docRef.id);

        // If this is a subcategory, update parent's subCategories array
        if (isSubcategory && data.parentCategory?.categoryId) {
          const parentCategoryRef = doc(
            db,
            "categories",
            data.parentCategory.categoryId
          );
          const parentCategoryDoc = await getDoc(parentCategoryRef);

          if (parentCategoryDoc.exists()) {
            const parentData = parentCategoryDoc.data();
            const subCategories = parentData.subCategories || [];

            await updateDoc(parentCategoryRef, {
              subCategories: [...subCategories, docRef.id],
            });
          }
        }

        // Add an audit log for the creation
        await addDoc(collection(db, "audit_logs"), {
          categoryId: docRef.id,
          categoryName: data.categoryName,
          previousData: null, // No previous data since it's new
          action: "Created",
          timestamp: serverTimestamp(),
        });
      }

      setLoader(false);
      const wasNewCategory = !categoryTabInfo?.selectedCategory?.id;
      setCategoryTabInfo((prevState) => ({
        ...prevState,
        addCategory: false,
        selectedCategory: null,
        page: wasNewCategory ? 1 : prevState.page || 1,
        trigger: !prevState?.trigger,
      }));
      setHeaderData();
    } catch (e) {
      setLoader(false);
      console.error("Error processing category: ", e);
    }
  };

  useEffect(() => {
    if (isInitialRender.current) {
      // Skip the effect on the initial render
      isInitialRender.current = false;
      return;
    }
    handleSubmit(AddNewCategories)();
  }, [categoryTabInfo?.trigger]);

  const fetchCategories = async () => {
    setLoader(true);
    try {
      const categoriesCollectionRef = collection(db, "categories");
      const querySnapshot = await getDocs(categoriesCollectionRef);
      const categoriesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const withCounts = await attachProductCounts(
        categoriesData,
        categoriesData
      );
      setCategories(withCounts);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSelectCategory = (category) => {
    setValue("parentCategory", {
      categoryName: category?.categoryName,
      categoryId: category?.id,
    });
    setIsModalOpen(false);
  };

  // Responsive styles
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        container: {
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          width: "95%",
          margin: "20px auto",
        },
        sidebar: {
          display: "none", // Hidden on mobile, replaced with drawer
        },
        content: {
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        },
        scrollContent: {
          height: "auto",
          overflowY: "visible",
        },
        section: {
          padding: "16px",
          borderRadius: "8px",
        },
        imageGrid: {
          gap: "8px",
        },
        imagePreview: {
          width: "80px",
          height: "80px",
        },
        formFields: {
          flexDirection: "column",
        },
        bannerHeight: "140px",
      };
    } else if (isTablet) {
      return {
        container: {
          display: "flex",
          gap: "20px",
          width: "95%",
          margin: "30px auto",
        },
        sidebar: {
          width: "35%",
          height: "max-content",
          background: "white",
          borderRadius: "8px",
          padding: "20px",
        },
        content: {
          width: "75%",
        },
        scrollContent: {
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "80vh",
          overflowY: "scroll",
          gap: "20px",
        },
        section: {
          padding: "20px",
          borderRadius: "8px",
        },
        imageGrid: {
          gap: "10px",
        },
        imagePreview: {
          width: "90px",
          height: "90px",
        },
        formFields: {
          flexDirection: "row",
        },
        bannerHeight: "160px",
      };
    } else {
      // Desktop styles (original)
      return {
        container: {
          display: "flex",
          gap: "30px",
          width: "95%",
          margin: "30px auto",
        },
        sidebar: {
          height: "max-content",
          width: "35%",
          background: "white",
          borderRadius: "8px",
          padding: "23px",
        },
        content: {
          width: "80%",
        },
        scrollContent: {
          display: "flex",
          width: "100%",
          flexDirection: "column",
          height: "80vh",
          overflowY: "scroll",
          gap: "20px",
        },
        section: {
          padding: "23px",
          borderRadius: "8px",
        },
        imageGrid: {
          gap: "10px",
        },
        imagePreview: {
          width: "100px",
          height: "100px",
        },
        formFields: {
          flexDirection: "row",
        },
        bannerHeight: "180px",
      };
    }
  };

  const styles = getResponsiveStyles();

  // Mobile Navigation Component
  const MobileNavigation = () => (
    <>
      {isMobile && (
        <AppBar position="sticky" sx={{ backgroundColor: "#023DE1", mb: 2 }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Add Category
            </Typography>
          </Toolbar>
        </AppBar>
      )}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Quick Navigation
          </Typography>
          {quickMenu.map((data, index) => (
            <Box
              key={index}
              sx={{
                borderBottom: "1px solid #e6e6e6",
                py: 1.5,
                cursor: "pointer",
              }}
              onClick={() => handleScrollToSection(data.ref)}
            >
              <Typography
                variant="body1"
                sx={{
                  color: activeTab === data.ref ? "#023DE1" : "#888e91",
                  fontWeight: 600,
                }}
              >
                {data?.title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );

  console.log(watch(), errors);

  return (
    <>
      <MobileNavigation />
      <div style={styles.container}>
        {/* Desktop/Tablet Sidebar */}
        {!isMobile && (
          <div style={styles.sidebar}>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <h3 style={{ fontWeight: "500", marginBottom: "5px" }}>
                Quick Navigation
              </h3>
              {quickMenu.map((data, index) => (
                <div
                  style={{
                    borderBottom: "1px solid #e6e6e6",
                    padding: "12px 0",
                  }}
                  key={index}
                  onClick={() => handleScrollToSection(data.ref)}
                >
                  <h4
                    style={{
                      color: `${
                        activeTab === data.ref ? "#023DE1" : "#888e91"
                      }`,
                      fontWeight: `${activeTab === data.ref ? "600" : "600"}`,
                      cursor: "pointer",
                    }}
                  >
                    {data?.title}
                  </h4>
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          className="scroll-content"
          ref={scrollContainerRef}
          style={styles.scrollContent}
        >
          {/* Product Information Section */}
          <div
            id="productInfoRef"
            ref={productInfoRef}
            style={{
              width: "100%",
              height: "max-content",
              background: "white",
              ...styles.section,
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "25px" }}
            >
              <h3 style={{ fontWeight: "600" }}>Information</h3>
              <h4 style={{ fontWeight: "400", margin: "0px 0 -7px 0" }}>
                {isSubcategory ? "Subcategory Image" : "Category Image"}
              </h4>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  ...styles.imageGrid,
                }}
              >
                {images.length === 0 && !loading && (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: "none" }}
                      id="image-upload"
                      onChange={handleImageUpload}
                      disabled={loading}
                    />
                    <label htmlFor="image-upload">
                      <div
                        style={{
                          border: "1px solid #e6e6e6",
                          cursor: "pointer",
                          borderRadius: "10px",
                          padding: isMobile ? "20px" : "30px",
                          ...styles.imagePreview,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <CameraAltOutlinedIcon
                          sx={{
                            height: isMobile ? "30px" : "40px",
                            width: isMobile ? "30px" : "40px",
                          }}
                        />
                      </div>
                    </label>
                  </>
                )}

                {images.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      border: "1px solid #e6e6e6",
                      borderRadius: "10px",
                      overflow: "hidden",
                      ...styles.imagePreview,
                    }}
                  >
                    <AsyncImage
                      src={image}
                      alt={`Preview ${index}`}
                      style={{
                        ...styles.imagePreview,
                        objectFit: "contain",
                      }}
                      loader={
                        <div style={{ background: "rgb(230, 230, 230)" }} />
                      }
                      error={
                        <div style={{ background: "rgb(230, 230, 230)" }} />
                      }
                    />
                    <IconButton
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        color: "white",
                        backgroundColor: "#023DE1",
                        padding: isMobile ? "4px" : "8px",
                      }}
                      onClick={() => {
                        const updatedImages = images.filter(
                          (_, i) => i !== index
                        );
                        setImages(updatedImages);
                      }}
                      size={isMobile ? "small" : "medium"}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
                {loading && (
                  <div
                    style={{
                      border: "1px solid #e6e6e6",
                      cursor: "pointer",
                      borderRadius: "10px",
                      padding: isMobile ? "20px" : "30px",
                      ...styles.imagePreview,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress size={24} />
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "20px",
                  ...styles.formFields,
                  width: `${
                    isSubcategory ? "100%" : isMobile ? "100%" : "50%"
                  }`,
                }}
              >
                <Controller
                  name="categoryName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="outlined-basic"
                      label={
                        isSubcategory ? "Subcategory Name*" : "Category Name*"
                      }
                      variant="outlined"
                      fullWidth
                      error={!!errors.categoryName}
                      helperText={errors.categoryName?.message}
                      size={isMobile ? "medium" : "small"}
                    />
                  )}
                />

                {isSubcategory && (
                  <Controller
                    name="parentCategory"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="outlined-basic"
                        label="Parent Category*"
                        variant="outlined"
                        fullWidth
                        error={!!errors.parentCategory}
                        helperText={errors.parentCategory?.message}
                        onClick={() => setIsModalOpen(true)}
                        InputProps={{
                          readOnly: true,
                        }}
                        value={field.value?.categoryName || ""} // Display the name of the selected parent category
                        size={isMobile ? "medium" : "small"}
                      />
                    )}
                  />
                )}
              </div>
              {(categories?.length !== 1 ||
                !categoryTabInfo?.selectedCategory) && (
                <Controller
                  name="isSubcategory"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormControlLabel
                      sx={{ width: "max-content" }}
                      control={
                        <Checkbox
                          {...field}
                          checked={isSubcategory}
                          onChange={(e) => {
                            handleSubcategoryChange(e);
                            onChange(e);
                          }}
                        />
                      }
                      label="Add as subcategory"
                    />
                  )}
                />
              )}

              <Controller
                name="order"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    sx={{ width: isMobile ? "100%" : "50%" }}
                    id="outlined-basic"
                    label="Order"
                    variant="outlined"
                    type="number" // Ensure numeric input
                    value={value} // Handle null/undefined cases
                    onChange={(e) => {
                      // Convert string input to number
                      const numValue = parseInt(e.target.value, 10);
                      onChange(numValue);
                    }}
                    error={!!errors.order}
                    helperText={errors.order?.message}
                    size={isMobile ? "medium" : "small"}
                  />
                )}
              />
            </div>
          </div>

          {/* Product Media Section */}
          <div
            id="productMediaRef"
            ref={productMediaRef}
            style={{
              width: "100%",
              height: "max-content",
              minHeight: isMobile ? "350px" : "400px",
              background: "white",
              ...styles.section,
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "25px" }}
            >
              <h3 style={{ fontWeight: "600" }}>Banner</h3>
              <h4
                style={{
                  fontWeight: "400",
                  margin: "-7px 0 -7px 0",
                  color: "gray",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                Content heading and description
              </h4>

              <ReactQuill
                theme="snow"
                value={value1}
                onChange={setValue1}
                style={{
                  width: "100%",
                  height: isMobile ? "150px" : "200px",
                  background: "white",
                  borderRadius: "8px",
                }}
              />

              <div style={{ paddingBottom: "20px" }}></div>
            </div>
          </div>
        </div>

        <CategorySelectionModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          categories={categories}
          onSelectCategory={handleSelectCategory}
          parentCategory={watch("parentCategory")}
          selectedCategoryId={categoryTabInfo?.selectedCategory?.id || ""}
        />
      </div>
    </>
  );
};

export default AddNewCategory;
